import { useCallback, useMemo, useState } from "react";

import type { DialDevice } from "services/dialstack";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

// Per BROWSER, on purpose (ENG-2183): the question is where the person is
// sitting. At the front desk they want the handset; the same person working
// from home has only the computer. Suffixed with the user id all the same —
// front-desk machines are shared and logout does not clear the key.
const STORAGE_KEY = "suno_softphone_dial_device_v1_";

const isDialDevice = (value: unknown): value is DialDevice =>
  value === "softphone" || value === "deskphone";

const isValidPreference = (data: unknown): boolean =>
  !!data && isDialDevice((data as { device?: unknown }).device);

export const otherDialDevice = (device: DialDevice): DialDevice =>
  device === "deskphone" ? "softphone" : "deskphone";

// Which of the user's devices a dial from this browser is meant for. Read
// through the repo's setupLocalStorageAPI (try/catch wrapped), never raw
// localStorage. Default: the computer, as before this preference existed.
export function useDialDevicePreference(userId: number | null) {
  // -1 / null is the pre-auth placeholder: never read or write a key for it.
  const storageKey = userId && userId > 0 ? `${STORAGE_KEY}${userId}` : null;
  const storage = useMemo(
    () => (storageKey ? setupLocalStorageAPI(storageKey) : null),
    [storageKey]
  );

  const readStored = (): DialDevice => {
    // getData returns UNDEFINED (not the fallback) when JSON.parse throws.
    const stored = storage?.getData(
      { device: "softphone" },
      isValidPreference
    ) as { device: DialDevice } | undefined;
    return stored?.device ?? "softphone";
  };

  const [device, setDevice] = useState<DialDevice>(readStored);

  // The user id arrives after first mount on a fresh page load; re-read the
  // right key when it does, without committing a wrong-state frame.
  const [initializedForKey, setInitializedForKey] = useState(storageKey);
  if (initializedForKey !== storageKey) {
    setInitializedForKey(storageKey);
    setDevice(readStored());
  }

  const setDialDevice = useCallback(
    (next: DialDevice) => {
      setDevice(next);
      storage?.updateData({ device: next });
    },
    [storage]
  );

  return [device, setDialDevice] as const;
}
